/**
 * @file   src\components\AddDeviceModal.tsx
 * @brief  Add Device Modal component.
 * @date   Jun, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { React, Modal, Button, useState, useEffect, useCallback } from '../utils/thirdpartyComponents';
import CWSelect from './CWSelect';
import '../components/CWModal.scss';
import { addDeviceIDSchema } from '../validations/addDeviceIDSchema';
import * as Yup from 'yup';
import { deviceidFormData } from "../interfaces/generalInterface";
import { EquipmentApi } from "../utils/apiUrls";
import { MessageToaster } from "../utils/toastUtil";
import axios from '../config/axiosConfig';
import SmallLoader from '../components/SmallLoader';
import { userType } from '../utils/enums';
import { DeviceOption, DeviceItem } from '../interfaces/CarWashInterface';

// Toast object creation.

const AddDeviceModal = (props: any) => {

    const { show, onHide, bayID, carwash_id } = props;
    const toaster = new MessageToaster();
    const [loading, setLoading] = useState(false);

    const handleOnHide = () => {
        onHide();
    }
    const [deviceOptions, setdeviceOptions] = useState<DeviceOption[]>([]);
    // Initialise states for fromdata
    const [formData, setFormData] = useState<deviceidFormData>({
        deviceId: 0
    });

    // Initialise states for errors
    const [errors, setErrors] = useState({
        deviceId: '',

    });

    // Triggers on input change
    const handleSelectChange = (selectedOption: any, name: string) => {

        const value = selectedOption?.value || '';
        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        setFormData({
            ...formData,
            [name]: value,
        });

    }

    const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        try {
            setLoading(true); // Show loader
            // Validate form data
            await addDeviceIDSchema.validate(formData, { abortEarly: false });
            try {
                const assignPayload: Record<string, unknown> = {
                    deviceId: formData.deviceId,
                    bayId: parseInt(bayID, 10),
                    locationId: props.location_id,
                };
                if (props.deviceType != null) {
                    assignPayload.deviceType = Number(props.deviceType);
                }
                const responseLocationEquipmentUpdate = (props.cuserType !== userType['CARWASH-OWNER'])
                    ? await axios.post(EquipmentApi.UPDATE_EQUIPMENT_DEVICE_ID, assignPayload)
                    :
                    await axios.post(EquipmentApi.CARWASH_OWNER_UPDATE_EQUIPMENT_DEVICE_ID, assignPayload)
                    ;
                if (responseLocationEquipmentUpdate?.data?.error === 0) {
                    setErrors({
                        deviceId: '',

                    });
                    onHide();
                }
                else {
                    toaster.toastError(responseLocationEquipmentUpdate?.data?.message);
                }
            } catch{
                toaster.toastError('Failed to update device.');
            }
        } catch (error) {

            const newErrors: any = {};

            //If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {

                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }

        } finally {
            setLoading(false); // Hide loader
        }

    }

    const getDevices = useCallback(async () => {
        try {
            setLoading(true);
            const bayIdSeg = String(bayID ?? '0');
            const carwashIdSeg = String(carwash_id ?? '0');
            const deviceTypeParam =
                props.deviceType != null ? Number(props.deviceType) : 0;
            // Admin: /admin/bay/getWashBayDevices/{carwashId}/{bayId}?deviceType=…
            const { data } =
                props.cuserType !== userType['CARWASH-OWNER']
                    ? await axios.get(
                          `${EquipmentApi.DEVICE_LISTING_API}${carwashIdSeg}/${bayIdSeg}`,
                          { params: { deviceType: deviceTypeParam } },
                      )
                    : await axios.get(
                          `${EquipmentApi.CARWASH_OWNER_DEVICE_LISTING_API}${bayIdSeg}`,
                          { params: { deviceType: deviceTypeParam } },
                      );
            if (data.error === 0) {
                const device_list: DeviceItem[] = data?.data ?? [];

                const transformedArray: DeviceOption[] = device_list.map((item) => ({
                    label: item.deviceName,
                    value: item.washBayDeviceID,
                }));

                setdeviceOptions(transformedArray);
            } else {
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        } finally {
            setLoading(false);
        }
    }, [bayID, carwash_id, props.cuserType, props.deviceType]);

    useEffect(() => {
        if (show === true) {
            getDevices();
        }
    }, [show, getDevices]);
    return (
        <>
            <Modal show={show} onHide={handleOnHide} centered className='add-modal'>
                <Modal.Body>
                    <h5 className='f-bold'>Add Device</h5>
                    <form onSubmit={handleSave}>

                        <CWSelect
                            id="deviceid"
                            name="deviceid"
                            options={deviceOptions}
                            label="Select Device"
                            dropdownGap={0}
                            dropdownPosition="auto"
                            placeholder="Select"
                            multi={false}
                            autoFocus
                            isMandatory
                            errorMessage={errors.deviceId}
                            onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'deviceId')}
                            className="no-input"
                        />
                        <div className="btn-sec d-grid">
                            <Button title="Cancel" variant="secondary" className='secondary-btn' onClick={handleOnHide}>Cancel</Button>
                            <Button title="Save" variant="primary" className='primary-btn' type='submit'>Save</Button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
            {loading && <SmallLoader />}
        </>
    );
};
export default AddDeviceModal;
